import { useEffect, useState } from "react";
import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import {
  resolveBodyColor,
  resolveBodyFontFamily,
  resolveHeadingColor,
  resolveHeadingFontFamily,
} from "../theme/themeValueResolver";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveWbChrome } from "../locale/wbChrome";
import {
  type BorderSide,
  type RuntimeProps,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  getBorderClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { type WBReveal } from "./reveal";

function getTimeRemaining(
  endTime: Date | string | null | undefined,
  showDays: boolean,
  showHours: boolean,
  showMinutes: boolean,
  showSeconds: boolean,
) {
  const zero = { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  if (!endTime) return zero;
  const date = endTime instanceof Date ? endTime : new Date(String(endTime));
  if (Number.isNaN(date.getTime())) return zero;

  const total = date.getTime() - new Date().getTime();
  if (total <= 0) return zero;

  let remainingMs = total;
  let days = 0;
  if (showDays) {
    days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
    remainingMs -= days * 1000 * 60 * 60 * 24;
  }

  let hours = 0;
  if (showHours) {
    hours = Math.floor(remainingMs / (1000 * 60 * 60));
    remainingMs -= hours * 1000 * 60 * 60;
  }

  let minutes = 0;
  if (showMinutes) {
    minutes = Math.floor(remainingMs / (1000 * 60));
    remainingMs -= minutes * 1000 * 60;
  }

  let seconds = 0;
  if (showSeconds) {
    seconds = Math.floor(remainingMs / 1000);
  }

  return { total, days, hours, minutes, seconds };
}

export function CountdownChild({
  isVisible = true,
  rounded,
  fontColorDate,
  fontColor,
  fontTypeDate,
  fontType,
  sizeDate = "text-lg",
  size = "text-sm",
  targetDate = null,
  layout = "horizontal",
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distItems = "gap-4",
  borderRadius,
  border = false,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides,
  showDays = true,
  showHours = true,
  showMinutes = true,
  showSeconds = true,
  showSeparator = true,
}: {
  isVisible?: boolean;
  rounded?: string;
  fontColorDate?: string;
  fontColor?: string;
  fontTypeDate?: string;
  fontType?: string;
  sizeDate?: string;
  size?: string;
  targetDate?: Date | string | null;
  layout?: "vertical" | "horizontal" | "grid" | "inline";
  padding?: string;
  distItems?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: ("top" | "right" | "bottom" | "left")[];
  showDays?: boolean;
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
  showSeparator?: boolean;
}) {
  const {
    currentRounding,
    currentColorPalette,
    currentFonts,
    bodyTheme,
    headingsTheme,
  } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const unitLabels = resolveWbChrome(currentLang, primaryLanguage);
  const [timeLeft, setTimeLeft] = useState(
    getTimeRemaining(targetDate, showDays, showHours, showMinutes, showSeconds),
  );

  useEffect(() => {
    const update = () => {
      setTimeLeft(
        getTimeRemaining(
          targetDate,
          showDays,
          showHours,
          showMinutes,
          showSeconds,
        ),
      );
    };
    update();
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [targetDate, showDays, showHours, showMinutes, showSeconds]);

  if (!isVisible) return null;

  const allTimeItems = [
    { label: unitLabels.days, value: timeLeft.days, key: "days", visible: showDays },
    { label: unitLabels.hours, value: timeLeft.hours, key: "hours", visible: showHours },
    {
      label: unitLabels.minutes,
      value: timeLeft.minutes,
      key: "minutes",
      visible: showMinutes,
    },
    {
      label: unitLabels.seconds,
      value: timeLeft.seconds,
      key: "seconds",
      visible: showSeconds,
    },
  ];
  const timeItems = allTimeItems.filter((item) => item.visible);

  const layoutStyles = {
    horizontal: "flex flex-row justify-center flex-wrap",
    vertical: "flex flex-col items-center",
    grid: "grid grid-cols-2 sm:grid-cols-4",
    inline: "flex flex-row justify-center",
  };

  const dateColor = resolveHeadingColor({
    override: fontColorDate,
    level: "h2",
    headingsTheme,
    palette: currentColorPalette,
  });
  const labelColor = resolveBodyColor({
    override: fontColor,
    bodyTheme,
    palette: currentColorPalette,
  });
  const itemBorderColor = borderColor ?? currentColorPalette.secondary;
  const dateFontFamily = resolveHeadingFontFamily({
    override: fontTypeDate,
    level: "h2",
    headingsTheme,
    fonts: currentFonts,
  });
  const labelFontFamily = resolveBodyFontFamily({
    override: fontType,
    fonts: currentFonts,
  });
  const roundingClass = rounded ?? borderRadius ?? currentRounding;

  return (
    <div className={classNames("w-full", padding)}>
      <div
        className={classNames(
          layoutStyles[layout] ?? layoutStyles.horizontal,
          distItems,
          "items-center",
        )}
      >
        {timeItems.map(({ label, value, key }, i) => (
          <div key={key} className="flex items-center">
            <div
              className={classNames(
                "flex flex-col items-center min-w-15 py-2 px-3",
                getBorderClasses(border, borderWidth, borderStyle, borderSides),
                border ? roundingClass : undefined,
              )}
              style={{
                borderColor: itemBorderColor,
                borderRadius: toCssLength(border ? roundingClass : undefined),
              }}
            >
              <span
                className={classNames(sizeDate, "font-bold leading-tight")}
                style={{
                  color: dateColor,
                  fontFamily: dateFontFamily,
                }}
              >
                {value.toString().padStart(2, "0")}
              </span>
              <span
                className={classNames(
                  size,
                  "font-medium uppercase tracking-wider mt-1 opacity-70",
                )}
                style={{
                  color: labelColor,
                  fontFamily: labelFontFamily,
                }}
              >
                {label}
              </span>
            </div>
            {showSeparator &&
            i < timeItems.length - 1 &&
            layout !== "vertical" &&
            layout !== "grid" ? (
              <span
                className={classNames(
                  sizeDate,
                  "font-bold mx-1 self-start mt-2",
                )}
                style={{
                  color: dateColor,
                  fontFamily: dateFontFamily,
                }}
              >
                :
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Countdown(props: RuntimeProps) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;

  const targetDate = props.countdownTargetDate
    ? new Date(String(props.countdownTargetDate))
    : null;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButton = (props.distTextButton as string | undefined) ?? "mb-2";
  const distButtonCountdown =
    (props.distButtonCountdown as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const buttonAlign =
    (props.buttonAlign as "left" | "center" | "right" | undefined) ?? "center";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
          (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined) ||
            (props.isVisibleButton as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distButtonCountdown,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames("flex flex-col justify-center", leftWidth)}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                fontFamily={props.fontFamilHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                fontFamily={props.fontFamilyHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <div className={distTextButton}>
              <Element
                is={TextChild}
                id="text"
                isVisible={props.isVisibleText as boolean | undefined}
                text={props.textText as string | undefined}
                fontSize={props.fontSizeText as string | undefined}
                fontFamily={props.fontFamilyText as string | undefined}
                textAlign={
                  props.textAlignText as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
              />
            </div>
            <div
              className={
                buttonAlign === "left"
                  ? "flex justify-start"
                  : buttonAlign === "center"
                    ? "flex justify-center"
                    : "flex justify-end"
              }
            >
              <Element is={Button} id="button" />
            </div>
          </div>
          <div className={rightWidth}>
            <Element
              id="countdown"
              is={CountdownChild}
              isVisible={Boolean(props.isVisiblCountdown ?? true)}
              rounded={props.countdownRounded as string | undefined}
              targetDate={targetDate}
              fontTypeDate={props.countdownFontTypeDate as string | undefined}
              fontColorDate={props.countdownFontColorDate as string | undefined}
              sizeDate={props.countdownSizeDate as string | undefined}
              fontType={props.countdownFontType as string | undefined}
              fontColor={props.countdownFontColor as string | undefined}
              size={props.countdownSize as string | undefined}
              borderColor={props.countdownBorderColor as string | undefined}
            />
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
